import { Controller } from "@hotwired/stimulus"

// welcome-grow
// ---------------------------------------------------------------
// Entrance for the onboarding welcome mascot. Entrance and idle are one
// animation: the "Skip & Shout" CSS loop (onboarding.css) starts at the top
// of a skip (--welcome-phase), so the mascot fades in while dropping, lands
// with the star burst and the FloosYo letter jelly, and keeps skipping.
//
//   1. Mascot fades in mid-air as .is-looping starts the loop
//   2. Title lines rise in so they are settled by the first landing
//
// Honors prefers-reduced-motion (static reveal, no loop).
export default class extends Controller {
  static targets = ["grower", "line"]
  static values = {
    // Cold-launch hold: the native splash stays up for min 1.2s after it
    // appears plus a 0.35s cross-dissolve (SplashViewController /
    // SceneDelegate), and the page renders behind it. Without this delay
    // the entrance plays hidden and the user lands on an already-settled
    // mascot. Tune via data-welcome-grow-delay-value if splash timing changes.
    delay: { type: Number, default: 1000 }
  }

  connect() {
    this._timers = []
    this._anims = []

    if (this._reducedMotion()) return

    // Hide the mascot and title during the delay so neither flashes in
    // before the entrance starts.
    if (this.hasGrowerTarget) this.growerTarget.style.opacity = "0"
    this.lineTargets.forEach((line) => { line.style.opacity = "0" })

    this._later(() => this._play(), this.delayValue)
  }

  disconnect() {
    this._timers.forEach((t) => clearTimeout(t))
    this._anims.forEach((a) => { try { a.cancel() } catch (_) {} })
    this._timers = []
    this._anims = []
    // Turbo snapshots the DOM as it stands; drop the loop so a restored
    // snapshot replays the entrance instead of starting mid-loop.
    this.element.classList.remove("is-looping")
  }

  // ── helpers ──────────────────────────────────────────────
  _reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _later(fn, ms) {
    const t = setTimeout(fn, ms)
    this._timers.push(t)
    return t
  }

  _animate(el, frames, opts) {
    const a = el.animate(frames, opts)
    this._anims.push(a)
    return a
  }

  // ── sequence ─────────────────────────────────────────────
  _play() {
    // Release the pre-animation holds set in connect(). The animations
    // created below take over in the same frame (fill: "backwards" covers
    // any delay), and leaving these inline would hide the mascot / lines
    // again once the animations finish.
    if (this.hasGrowerTarget) this.growerTarget.style.opacity = ""
    this.lineTargets.forEach((line) => { line.style.opacity = "" })

    // Start the loop at the top of a skip; the fade covers the drop so the
    // mascot appears in the air and lands ~0.34s later.
    this.element.classList.add("is-looping")
    if (this.hasGrowerTarget) {
      this._animate(
        this.growerTarget,
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 220, easing: "ease-out", fill: "backwards" }
      )
    }

    // Title lines rise in alongside the drop so they are in place when the
    // landing sets off the letter jelly.
    this.lineTargets.forEach((line, i) => {
      this._animate(
        line,
        [
          { transform: "translateY(12px)", opacity: 0 },
          { transform: "translateY(0)",    opacity: 1 }
        ],
        { duration: 380, delay: i * 90, easing: "cubic-bezier(0.2, 0.9, 0.3, 1)", fill: "backwards" }
      )
    })
  }
}
